import * as Yup from "yup";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import Link from "@mui/material/Link";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import InputAdornment from "@mui/material/InputAdornment";

import { paths } from "src/routes/paths";
import { RouterLink } from "src/routes/components";
import { useRouter, useSearchParams } from "src/routes/hooks";

import { useBoolean } from "src/hooks/use-boolean";

import { useAuthContext } from "src/auth/hooks";
import { PATH_AFTER_LOGIN } from "src/config-global";

import Iconify from "src/components/iconify";
import FormProvider, { RHFTextField } from "src/components/hook-form";
import { t } from "i18next";
import RegistrationDialog from "src/components/custom-dialog/registration-dialog";

// ----------------------------------------------------------------------

export default function JwtLoginView({
  onClose,
  returnTo: returnToProp,
}: {
  onClose: () => void;
  returnTo?: string;
}) {
  const { login } = useAuthContext();

  const router = useRouter();

  const [errorMsg, setErrorMsg] = useState("");

  const searchParams = useSearchParams();
  const returnTo = returnToProp || searchParams.get("returnTo");

  const currentPath =
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}`
      : "/";

  const password = useBoolean();

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .required("Email is required")
      .email("Email must be a valid email address"),
    password: Yup.string().required("Password is required"),
  });

  const defaultValues = {
    email: "",
    password: "",
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    resetField,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      await login?.(data.email, data.password);

      const target = returnTo ? decodeURIComponent(returnTo) : "/";
      const shouldRefresh = target.includes("/calendar");

      setTimeout(() => {
        onClose?.();

        if (!shouldRefresh) {
          router.replace(target);
        } else {
          router.replace(
            `${target}${target.includes("?") ? "&" : "?"}refresh=1`
          );
        }
      }, 50);
    } catch (error) {
      console.error(error);
      resetField("password");
      setErrorMsg(typeof error === "string" ? t(error) : t(error?.detail));
    }
  });

  const renderForm = (
    <Stack spacing={2.5}>
      {!!errorMsg && <Alert severity="error">{errorMsg}</Alert>}

      <RHFTextField
        sx={{ mt: 1 }}
        name="email"
        label={t("email_address")}
        autoComplete="email"
      />

      <RHFTextField
        name="password"
        label={t("password")}
        autoComplete="current-password"
        type={password.value ? "text" : "password"}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={password.onToggle} edge="end">
                <Iconify
                  icon={
                    password.value ? "solar:eye-bold" : "solar:eye-closed-bold"
                  }
                />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* <RegistrationDialog text={"no_registration"} color={"info"} /> */}

      <Link
        variant="body2"
        href={paths.auth.jwt.forgotPassword}
        component={RouterLink}
        color="inherit"
        underline="always"
        sx={{ alignSelf: "flex-end" }}
      >
        {t("forgot_password")}?
      </Link>

      <Stack>
        <LoadingButton
          fullWidth
          color="primary"
          size="large"
          type="submit"
          variant="contained"
          loading={false}
          disabled={isSubmitting}
          sx={{ mb: 3 }}
        >
          {isSubmitting ? t("loading") : t("login")}
        </LoadingButton>
      </Stack>
    </Stack>
  );

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      {/* {renderHead} */}

      {renderForm}
    </FormProvider>
  );
}
